import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useOptimizedImage } from '../../hooks/useOptimizedImage';
import { imageCache } from '../../utils/imageCache';

const UserAvatar = ({
  user,
  size = 'w-10 h-10',
  textSize = 'text-lg',
  className = '',
  showBorder = false,
  borderColor = 'border-white/20',
  fallbackColors = 'from-blue-400 to-purple-500',
  rounded = 'rounded-full',
  clickable = true,
  onClick = null,
  fallbackIcon = null
}) => {
  const navigate = useNavigate();
  
  // Create cache key for this avatar
  const avatarUrl = user?.avatar_url || user?.avatarUrl;
  const cacheKey = user?.id ? imageCache.getUserAvatarKey(user.id, size, avatarUrl) : null;
  
  // Use optimized image loading with lazy loading disabled for avatars (usually above fold)
  const {
    imageLoaded,
    imageError,
    isVisible,
    imgRef,
    containerRef,
    src,
    onLoad,
    onError
  } = useOptimizedImage(avatarUrl, cacheKey, false);

  // Size mapping for Tailwind classes to pixels
  const SIZE_MAP = {
    'w-6 h-6': '24px',
    'w-8 h-8': '32px',
    'w-10 h-10': '40px',
    'w-12 h-12': '48px',
    'w-14 h-14': '56px',
    'w-16 h-16': '64px',
    'w-20 h-20': '80px',
    'w-24 h-24': '96px',
    'w-32 h-32': '128px',
    'w-40 h-40': '160px',
    'w-48 h-48': '192px',
    'w-56 h-56': '224px',
    'w-64 h-64': '256px',
    'w-80 h-80': '320px'
  };

  // Font size mapping for Tailwind classes to pixels
  const FONT_SIZE_MAP = {
    'text-sm': '14px',
    'text-base': '16px',
    'text-lg': '18px',
    'text-xl': '20px',
    'text-2xl': '24px',
    'text-3xl': '30px',
    'text-4xl': '36px',
    'text-5xl': '48px',
    'text-6xl': '60px',
    'text-7xl': '72px',
    'text-8xl': '96px',
    'text-9xl': '128px'
  };

  // Extract base size from responsive classes (e.g., "w-24 h-24 sm:w-32 sm:h-32" -> "w-32 h-32")
  const extractActualSize = (sizeClass) => {
    // If it contains responsive classes, extract the largest size (sm: prefix means 640px+)
    if (sizeClass.includes('sm:')) {
      const smMatch = sizeClass.match(/sm:(w-\d+ h-\d+)/);
      if (smMatch) return smMatch[1];
    }
    // Extract base size
    const baseMatch = sizeClass.match(/w-\d+ h-\d+/);
    return baseMatch ? baseMatch[0] : 'w-10 h-10';
  };

  // Get pixel values using the largest responsive size
  const actualSize = extractActualSize(size);
  const sizeInPx = SIZE_MAP[actualSize] || '40px';
  const fontSizeInPx = FONT_SIZE_MAP[textSize] || '16px';
  
  // Get fallback initial
  const getFallbackInitial = () => {
    return user?.username?.charAt(0)?.toUpperCase() ||
        user?.displayName?.charAt(0)?.toUpperCase() ||
        user?.display_name?.charAt(0)?.toUpperCase() ||
        user?.name?.charAt(0)?.toUpperCase() ||
        'U';
  };

  // Handle click events
  const handleClick = () => {
    if (onClick) {
      onClick(user);
    } else if (clickable && user?.id) {
      navigate(`/profile/${user.id}`);
    }
  };

  // Image events are handled by the hook

  // Common click props
  const clickProps = (clickable || onClick) ? {
    onClick: handleClick,
    className: `${className} cursor-pointer hover:opacity-80 transition-opacity`,
    role: 'button',
    tabIndex: 0,
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    }
  } : { className };

  // Optional neutral "no image" icon fallback (matches TeamLogo's placeholder),
  // used instead of gradient + initials when a fallbackIcon is provided.
  const iconPx = Math.round(parseInt(sizeInPx, 10) * 0.55);
  const sizedFallbackIcon = fallbackIcon
    ? React.cloneElement(fallbackIcon, { style: { width: `${iconPx}px`, height: `${iconPx}px`, color: '#9ca3af' } })
    : null;

  // Common fallback styles
  const fallbackStyles = {
    width: sizeInPx,
    height: sizeInPx,
    borderRadius: rounded === 'rounded-full' ? '50%' : '8px',
    background: sizedFallbackIcon ? '#22262f' : 'linear-gradient(135deg, #60a5fa, #a855f7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: fontSizeInPx,
    fontWeight: 'bold',
    color: 'white',
    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
    ...(showBorder && {
      border: '2px solid rgba(255, 255, 255, 0.2)'
    })
  };

  // Use the optimized loading state from the hook
  const showPlaceholder = !imageLoaded || !isVisible;

  // Create fallback element
  const FallbackAvatar = ({ isLoading = false }) => (
    <div
      className={`flex items-center justify-center text-white font-bold ${isLoading ? 'animate-pulse' : ''}`}
      style={fallbackStyles}
      {...clickProps}
    >
      {sizedFallbackIcon || getFallbackInitial()}
    </div>
  );

  // Show fallback if no avatar URL or image failed
  if (!src || imageError) {
    return <FallbackAvatar />;
  }

  // Show image with loading fallback
  return (
    <div ref={containerRef} className="relative overflow-hidden" style={{ width: sizeInPx, height: sizeInPx, position: 'relative' }} {...clickProps}>
      {isVisible && (
        <img
          ref={imgRef}
          src={src}
          alt={`${user.username || user.display_name || 'User'}'s avatar`}
          className="object-cover"
          onError={onError}
          onLoad={onLoad}
          loading="lazy" // Lazy load images
          decoding="async" // Async decode for better performance
          style={{
            width: sizeInPx,
            height: sizeInPx,
            borderRadius: rounded === 'rounded-full' ? '50%' : '8px',
            display: imageError ? 'none' : 'block',
            opacity: imageLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
            position: 'relative',
            zIndex: 2,
            ...(showBorder && {
              border: '2px solid rgba(255, 255, 255, 0.2)'
            })
          }}
        />
      )}
      {/* Loading placeholder - only show before image loads */}
      {showPlaceholder && !imageError && src && (
        <div 
          className="absolute inset-0 animate-pulse"
          style={{
            ...fallbackStyles,
            zIndex: 1
          }}
        >
          {sizedFallbackIcon || getFallbackInitial()}
        </div>
      )}
    </div>
  );
};

export default UserAvatar;